import React from 'react';
import PropTypes from 'prop-types';
import NVD3Chart from 'react-nvd3';
import nv from 'nvd3';
import d3 from 'd3';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import sortBy from 'lodash/sortBy';
import debounce from 'lodash/debounce';
import flatten from 'lodash/flatten';

import 'nvd3/build/nv.d3.css';
import alpha from 'color-alpha';

import Legend from './Legend';
import { initZoom, initNv, formatCharData, ADVANCED_SYMBOLS } from './utils';

const complexScattergram = {
  accepted: {
    series: 0,
    result: 'Accepted',
    type: '',
    shape: 'checkmark',
    color: '#009900',
    chartType: 'simple',
  },
  acceptedRD: {
    series: 0,
    result: 'Accepted',
    type: 'RD',
    shape: 'checkmark',
    color: '#009900',
    chartType: 'advanced',
  },
  acceptedED: {
    series: 1,
    result: 'Accepted',
    type: 'ED',
    shape: 'circle-check',
    color: '#009900',
    chartType: 'advanced',
  },
  acceptedEA: {
    series: 2,
    result: 'Accepted',
    type: 'EA',
    shape: 'checksquare',
    color: '#009900',
    chartType: 'advanced',
  },
  waitlistedAccepted: {
    series: 1,
    result: 'Waitlisted/Accepted',
    type: '',
    shape: 'rhombus',
    color: '#009900',
    chartType: 'simple',
  },
  waitlistedAcceptedRD: {
    series: 3,
    result: 'Waitlisted/Accepted',
    type: 'RD',
    shape: 'rhombus',
    color: '#009900',
    chartType: 'advanced',
  },
  waitlistedAcceptedED: {
    series: 4,
    result: 'Waitlisted/Accepted',
    type: 'ED',
    shape: 'circle-rhombus',
    color: '#009900',
    chartType: 'advanced',
  },
  waitlistedAcceptedEA: {
    series: 5,
    result: 'Waitlisted/Accepted',
    type: 'EA',
    shape: 'square-rhombus',
    color: '#009900',
    chartType: 'advanced',
  },
  waitlistedUnknown: {
    series: 2,
    result: 'Waitlisted/Unknown',
    type: '',
    shape: 'rhombus',
    color: '#000099',
    chartType: 'simple',
  },
  waitlistedUnknownRD: {
    series: 6,
    result: 'Waitlisted/Unknown',
    type: 'RD',
    shape: 'rhombus',
    color: '#000099',
    chartType: 'advanced',
  },
  waitlistedUnknownED: {
    series: 7,
    result: 'Waitlisted/Unknown',
    type: 'ED',
    shape: 'circle-rhombus',
    color: '#000099',
    chartType: 'advanced',
  },
  waitlistedUnknownEA: {
    series: 8,
    result: 'Waitlisted/Unknown',
    type: 'EA',
    shape: 'square-rhombus',
    color: '#000099',
    chartType: 'advanced',
  },
  waitlistedDenied: {
    series: 3,
    result: 'Waitlisted/Denied',
    type: '',
    shape: 'rhombus',
    color: '#990000',
    chartType: 'simple',
  },
  waitlistedDeniedRD: {
    series: 9,
    result: 'Waitlisted/Denied',
    type: 'RD',
    shape: 'rhombus',
    color: '#990000',
    chartType: 'advanced',
  },
  waitlistedDeniedED: {
    series: 10,
    result: 'Waitlisted/Denied',
    type: 'ED',
    shape: 'circle-rhombus',
    color: '#990000',
    chartType: 'advanced',
  },
  waitlistedDeniedEA: {
    series: 11,
    result: 'Waitlisted/Denied',
    type: 'EA',
    shape: 'square-rhombus',
    color: '#990000',
    chartType: 'advanced',
  },
  denied: {
    series: 4,
    result: 'Denied',
    type: '',
    shape: 'thick-x',
    color: '#990000',
    chartType: 'simple',
  },
  deniedRD: {
    series: 12,
    result: 'Denied',
    type: 'RD',
    shape: 'thick-x',
    color: '#990000',
    chartType: 'advanced',
  },
  deniedED: {
    series: 13,
    result: 'Denied',
    type: 'ED',
    shape: 'circle-x',
    color: '#990000',
    chartType: 'advanced',
  },
  deniedEA: {
    series: 14,
    result: 'Denied',
    type: 'EA',
    shape: 'square-x',
    color: '#990000',
    chartType: 'advanced',
  },
  you: { series: 15, result: 'You', shape: 'student', color: '#33AAFF' },
  avgGPA: { series: 16, result: 'Average GPA', shape: 'v-dash', color: '#000000' },
  avgScore: { series: 17, result: 'Average Score', shape: 'h-dash', color: '#000000' },
};

const actCompositeKeys = ['actComposite', 'actCompositeStudent'];

const satCompositeKeys = [
  'highestComboSat',
  'highestComboSatWWConvertedTo1600',
  'studentSATCompositeConvertedTo1600',
  'studentSAT1600Composite',
];

const UNDERSCORE = '_';
class Scattergram extends React.Component {
  static propTypes = {
    scattergrams: PropTypes.object,
    testType: PropTypes.string,
    gpaType: PropTypes.string,
    graphType: PropTypes.number,
    hsOptions: PropTypes.object,
    userData: PropTypes.object,
    appliedTestOptional: PropTypes.bool,
    enableTestOptionalFeature: PropTypes.bool,
  };

  constructor(props) {
    super(props);
    this.colorMap = Object.keys(complexScattergram).map(function (key) {
      return complexScattergram[key].color;
    });
    this.series = [];
    this.xValues = [];
    this.userIsUsingPSat = false;
    initNv();

    this.enableTestOptionalFeature = props.enableTestOptionalFeature;

    if (this.enableTestOptionalFeature) {
      const numberOfComplexScattergramKeys = Object.keys(complexScattergram).length - 5; // substract 5 parent nodes as their series is repeating in child nodes (0,1,2,3,4)
      Object.entries(complexScattergram)
        .filter(([key, value]) => value.type)
        .forEach(([key, value]) => {
          complexScattergram[`${key}TestOptional`] = {
            ...value,
            series: value.series + numberOfComplexScattergramKeys,
          };
        });
    }

    this.state = {
      data: this.generateData(props.testType, props.gpaType),
      options: this.createOptions(props.testType, props.gpaType),
    };
    this.chartRef = React.createRef();
  }

  componentDidMount() {
    window.addEventListener('resize', this.handleResize);
    initZoom(this.chartRef);
    // fix bug on first render when chart items get buggy
    this.forceRefresh();
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize);
  }

  handleResize = () => {
    let winWidth;
    let height;

    if (window.innerWidth < 920) {
      winWidth = window.innerWidth - 20;
    } else {
      winWidth = 900;
    }
    height = ((winWidth * 3) / 5).toFixed(0);

    this.chartRef.current.refs.root.style.height = `${height}px`;
  };

  componentDidUpdate(prevProps) {
    if (
      this.props.testType !== prevProps.testType ||
      this.props.gpaType !== prevProps.gpaType
    ) {
      initZoom(this.chartRef);
      this.reloadScattergram();
    }
  }

  /* istanbul ignore next */
  getExtraIconsTooltipLabel = (d) => {
    const { userIsUsingPSat } = this;
    const { testType } = this.props;
    const testTypeText =
      d.point.shape === 'student' && userIsUsingPSat ? 'PSAT' : testType.replace('_', '');
    return ' ' + testTypeText + ': ' + d.point.x + ', GPA: ' + d.point.y;
  };

  getTooltipLabelWithResult = (d, testType) => {
    return (
      ' ' +
      d.point.result.toUpperCase() +
      ' ' +
      d.point.type +
      (this.enableTestOptionalFeature && this.isAppTestOptional(d.point.isTestOptional)
        ? ', TEST OPTIONAL'
        : '') +
      (d.point.waitlisted || d.point.deferred ? '<br/>' : '') +
      (d.point.waitlisted ? ' waitlisted ' : '') +
      (d.point.waitlisted && d.point.deferred ? '/' : '') +
      (d.point.deferred ? ' deferred' : '') +
      '<br/>' +
      testType.replace('_', '') +
      ': ' +
      d.point.x +
      ', GPA: ' +
      d.point.y
    );
  };

  createOptions = (testType, gpaTypeIn) => {
    const { getExtraIconsTooltipLabel, getTooltipLabelWithResult, series } = this;
    let options = {
      type: 'scatterChart',
      showLegend: false,
      color: this.colorMap.concat([
        'rgba(0,0,0,0)',
        'rgba(0,0,0,0)',
        'rgba(0,0,0,0)',
        'rgba(0,0,0,0)',
      ]),
      pointRange: [1, 200],
      scatter: {
        onlyCircles: false,
      },
      tooltip: {
        contentGenerator: function (d) {
          /* istanbul ignore next */
          const index = get(d, 'seriesIndex', -1);
          /* istanbul ignore next */
          if (series[index] === false) {
            // hide tooltips of hidden dots
            return '<div/>';
          }

          if (d.point.result === '') {
            return getExtraIconsTooltipLabel(d);
          } else {
            return getTooltipLabelWithResult(d, testType);
          }
        },
      },
      duration: 350,
      showXAxis: true,
      xAxis: {
        axisLabel: testType.replace('_', ' '),
      },
      yAxis: {
        axisLabel: 'GPA',
        tickFormat: function (d) {
          return d3.format('.02f')(d);
        },
        axisLabelDistance: -5,
      },
    };

    return options;
  };

  getDataNode = (testTypeIn, gpaTypeIn) => {
    const dataNode = gpaTypeIn === 'WEIGHTED_GPA' ? 'weightedGpa' : 'gpa';
    const dataSubNode = testTypeIn === 'ACT' ? 'act' : 'sat';

    return `${dataNode}.${dataSubNode}`;
  };

  getMinXvalue = () =>
    this.props.testType === 'ACT'
      ? get(this.props, 'hsOptions.graphActMin', 10)
      : get(this.props, 'hsOptions.graphSatMin', 600);

  calcualteMinMaxValues = (values, testTypeIn) => {
    if (isEmpty(values)) {
      return [0, 0];
    }
    const min = this.getMinXvalue();
    const max = Math.max(...values);

    return [min, max];
  };

  getMaxValueByTestType = (item, testTypeIn) => {
    if (testTypeIn === 'ACT') {
      return Math.max(...actCompositeKeys.map((key) => get(item, key, 0)));
    }
    return Math.max(...satCompositeKeys.map((key) => get(item, key, 0)));
  };

  isCumulative = () => this.props.gpaType === 'CUMULATIVE_GPA';

  /* istanbul ignore next */
  getUserSatValue = () => {
    const satValue =
      get(this.props, 'userInfo.academics.sat', 0) ||
      get(this.props, 'userData.highestSAT', 0);

    if (satValue) {
      return satValue;
    }

    this.userIsUsingPSat = true;
    return get(this.props, 'userInfo.academics.psat', 0);
  };

  /**
   * When the user selects "No" from the "Submitting SAT or ACT Scores" dropdown (isTestOptional: 0),
   * only then it implies that he/she has opted for "Test Optional".
   * And, in the Scattergram page when the user checks the applied test optional checkbox,
   * every app other than the ones opting for "Test Optional" should fade out,
   * or we can say that the apps opting for "Test Optional" are highlighted (opposite to being faded).
   * Also, the "TEST OPTIONAL" tag should be added for the apps opting for "TEST OPTIONAL".
   *
   * In short,
   *    1. If the selected value from the dropdown is "No", the "TEST OPTIONAL" tag should be shown in the tooltip.
   *       [This is valid irrespective of whether or not the checkbox is checked]
   *    2. If the selected value from the dropdown is anything other than "No", the apps should fade out
   *       to highlight the "TEST OPTIONAL" apps (with the value "No"). [This is only valid when the checkbox is checked]
   *
   * @param {number} isTestOptional
   * @returns {boolean}
   */
  isAppTestOptional = (isTestOptional) => {
    return isTestOptional === 0;
  };

  generateData = (testTypeIn, gpaTypeIn) => {
    const scattergrams = get(this.props, 'scattergrams', {});
    const testTypeNode = get(scattergrams, this.getDataNode(testTypeIn, gpaTypeIn), {});

    // avoid calculations if type node data is null
    if (isEmpty(testTypeNode)) {
      return [];
    }

    const testTypeAvg = get(testTypeNode, 'avg', 0);
    const gpaAvg = get(testTypeNode, 'gpaAvg', 0);
    const apps = get(testTypeNode, 'apps', {});
    this.xValues = [];

    let appsData;
    if (this.enableTestOptionalFeature) {
      // segregating TestOptional data as NonTestOptional points need to be faded
      appsData = {};
      for (const appsKey in apps) {
        if (apps.hasOwnProperty(appsKey)) {
          const testOptionalKey = `${appsKey}TestOptional`;
          appsData[appsKey] = apps[appsKey].filter(
            (item) => !this.isAppTestOptional(item.isTestOptional),
          );
          const testOptionalData = apps[appsKey].filter((item) =>
            this.isAppTestOptional(item.isTestOptional),
          );
          if (testOptionalData.length > 0) {
            appsData[testOptionalKey] = testOptionalData;
          }
        }
      }
    } else {
      appsData = apps;
    }

    // we need to get the correct keys to avoid getting empty series
    const objectKeys = Object.keys(complexScattergram)
      .map((key) => {
        if (isEmpty(complexScattergram[key].chartType)) {
          return null;
        }
        if (this.isAdvanceSymbols()) {
          return key;
        }

        return complexScattergram[key].chartType === 'simple' ? key : null;
      })
      .filter((i) => i); // clean empty values

    const chartData = objectKeys.map((key) => {
      const { type, shape, result, color, series } =
        complexScattergram[key] || complexScattergram.waitlistedUnknown;

      let keyName = ` (${type})`;
      if (this.enableTestOptionalFeature && key.endsWith('TestOptional')) {
        keyName += ', TestOptional';
      }

      return {
        key: `${result}${type ? keyName : ''}`,
        series: series,
        values: (appsData[key] || [])
          .map((item) => {
            const xValue = this.getMaxValueByTestType(item, testTypeIn);
            const yValue = item['gpa'];
            this.xValues.push((yValue && xValue) || 0);

            // if it is the current user data
            if (item.currentStudent) {
              return undefined;
            }
            // dont show dots that hace empty data
            if (!xValue || !yValue) {
              return undefined;
            }

            // not show dots that are below min chart value
            if (xValue <= this.getMinXvalue()) {
              return undefined;
            }

            const appsItem = {
              x: xValue,
              y: yValue,
              size: 100,
              shape,
              result,
              series: series,
              type,
            };
            if (this.enableTestOptionalFeature) {
              appsItem['isTestOptional'] =
                item['isTestOptional'] !== undefined ? item['isTestOptional'] : null;
            }
            return appsItem;
          })
          .filter((i) => i),
        color,
      };
    });

    // if not dots available there is no need show the chart
    if (isEmpty(flatten(chartData.map((i) => i.values)))) {
      return [];
    }

    chartData.push({
      key: 'Student',
      values: [],
      color: '#33AAFF',
    });

    // current user data
    this.userIsUsingPSat = false;
    // try to get raw values
    const studentY = get(
      this.props,
      `userInfo.academics.${this.isCumulative() ? 'rawCumulativeGpa' : 'rawWeightedGpa'}`,
      get(
        this.props,
        `userData.${this.isCumulative() ? 'cumulativeGpa' : 'weightedGpa'}`,
        0,
      ),
    );
    const studentX =
      testTypeIn === 'ACT'
        ? get(
            this.props,
            'userInfo.academics.act',
            get(this.props, 'userData.highestACT', 0),
          )
        : this.getUserSatValue();

    if (studentX > 0 && studentY > 0) {
      chartData[chartData.length - 1].values.push({
        x: studentX,
        y: studentY,
        size: 200,
        shape: 'student',
        result: '',
        waitlisted: false,
        deferred: false,
        type: '',
      });
      // strange bug on nvd3 that freezes ui when student icon X value is the max X value on the chart,
      // we add 1 to avoid that
      /* istanbul ignore next */
      this.xValues.push(studentX + 1);
    }

    // calculate min max values including user tests values
    let [lowX, hiX] = this.calcualteMinMaxValues(this.xValues, testTypeIn);
    const [lowY, hiY] = [
      get(this.props, 'hsOptions.graphGpaMin', 0),
      get(this.props, 'hsOptions.graphGpaMax', 5) + 1,
    ];
    const hDashInterval = testTypeIn === 'ACT' ? 1 : 50;

    chartData.push({
      key: 'Avg ' + testTypeIn,
      values: [],
      color: '#000000',
    });

    for (let i = gpaAvg; i > lowY; i = i - 0.2) {
      chartData[chartData.length - 1].values.push({
        x: testTypeAvg,
        y: parseFloat(i.toFixed(2)),
        size: 10,
        shape: 'v-dash',
        result: '',
        waitlisted: false,
        deferred: false,
        type: '',
      });
    }

    chartData.push({
      key: 'Avg GPA',
      values: [],
      color: '#000000',
    });

    for (let i = testTypeAvg; i > lowX; i = i - hDashInterval) {
      chartData[chartData.length - 1].values.push({
        x: parseInt(i.toFixed(0)),
        y: gpaAvg,
        size: 10,
        shape: 'h-dash',
        result: '',
        waitlisted: false,
        deferred: false,
        type: '',
      });
    }

    chartData.push({
      key: 'Origin',
      values: [],
      color: 'rgba(0,0,0,0)',
    });

    chartData[chartData.length - 1].values.push({
      x: lowX,
      y: lowY,
      size: 200,
      shape: 'h-dash',
      result: '',
      waitlisted: false,
      deferred: false,
      type: '',
    });

    chartData[chartData.length - 1].values.push({
      x: hiX,
      y: hiY,
      size: 1,
      shape: 'thin-x',
      result: '',
      waitlisted: false,
      deferred: false,
      type: '',
    });

    return sortBy(chartData, ['series']);
  };

  toggleSeries = (series) => {
    if (typeof this.series[series] === 'undefined') {
      this.series[series] = true;
    }

    this.series[series] = !this.series[series];

    d3.select('#nvd3Scattergram')
      .select('.nv-group.nv-series-' + series)
      .style('display', this.series[series] ? 'inline' : 'none');

    if (this.enableTestOptionalFeature) {
      d3.select('#nvd3Scattergram')
        .select('.nv-group.nv-series-' + (series + 15))
        .style('display', this.series[series] ? 'inline' : 'none');
    }
  };

  /* istanbul ignore next */
  getSymbol = (symbol) => {
    if (!nv.utils.symbolMap[UNDERSCORE][symbol]) {
      return d3.svg.symbol().type(symbol).size(100)();
    } else {
      let size = symbol === 'student' ? 150 : symbol.includes('-dash') ? 10 : 75;
      return nv.utils.symbolMap[UNDERSCORE][symbol](size);
    }
  };
  /* istanbul ignore next */
  forceRefresh = debounce(() => {
    const chart = get(this.chartRef, 'current.chart', null);
    if (chart) {
      chart.update();
    }
  }, 500);

  reloadScattergram() {
    this.setState(
      {
        data: this.generateData(this.props.testType, this.props.gpaType),
        options: this.createOptions(this.props.testType, this.props.gpaType),
      },
      () => {
        this.forceRefresh();
      },
    );
  }

  isAdvanceSymbols = () => this.props.graphType === ADVANCED_SYMBOLS;

  getScattergramLegendKey = (key, seriesIndex) => {
    const item = complexScattergram[key];
    let resultText = item.result;
    const appType = item.type;

    const svgIcon =
      `<svg>` +
      `<g style="stroke-opacity: 1; fill-opacity: 0.5; stroke: ` +
      `${item.color}; fill: ${item.color};">` +
      `<path transform="translate(15,15)" d="${this.getSymbol(item.shape)}"></path>` +
      `</g></svg>`;

    if (appType) {
      if (appType === 'ED') {
        resultText = 'Early Decision';
      } else if (appType === 'EA') {
        resultText = 'Early Action';
      } else {
        resultText = 'Regular Decision';
      }
    }

    return {
      svgIcon,
      label: this.isAdvanceSymbols() || key === 'you' ? resultText : '',
      seriesIndex: !this.isAdvanceSymbols() && key === 'you' ? seriesIndex : item.series,
    };
  };

  getLegendColumn = (result) => {
    let filtered = Object.keys(complexScattergram)
      .map((key) => ({
        ...complexScattergram[key],
        key: key,
      }))
      .filter(
        (i) =>
          i.result === result &&
          i.chartType === (this.isAdvanceSymbols() ? 'advanced' : 'simple'),
      );

    if (this.enableTestOptionalFeature) {
      filtered = filtered.filter((item) => !item.key.includes('TestOptional'));
    }
    return {
      title: result,
      isToggle: true,
      items: filtered.map((f) => this.getScattergramLegendKey(f.key)),
    };
  };

  formatLegendData = (chartData) => {
    return [
      {
        title: 'Legend',
        isToggle: false,
        items: [
          this.getScattergramLegendKey('you', chartData.length - 4), // we calculate the series index for YOU option
          this.isAdvanceSymbols() ? this.getScattergramLegendKey('avgGPA') : null,
          this.isAdvanceSymbols() ? this.getScattergramLegendKey('avgScore') : null,
        ].filter((i) => i),
      },
      this.getLegendColumn('Accepted'),
      this.getLegendColumn('Waitlisted/Accepted'),
      this.getLegendColumn('Waitlisted/Unknown'),
      this.getLegendColumn('Waitlisted/Denied'),
      this.getLegendColumn('Denied'),
    ];
  };

  render() {
    const hasData = !isEmpty(this.state.data);
    const chartData = hasData
      ? formatCharData(this.state.data, this.isAdvanceSymbols())
      : [];

    if (this.enableTestOptionalFeature) {
      chartData.forEach((x) => {
        if (!x.key.includes('TestOptional')) {
          const opacity = this.props.appliedTestOptional ? 0.1 : 1;
          const modifiedColor = alpha(x.color, opacity);
          if (modifiedColor) x.color = modifiedColor;
        }
      });
    }
    return (
      <div id="nvd3Scattergram">
        {hasData ? (
          <>
            <NVD3Chart
              ref={this.chartRef}
              type="scatterChart"
              containerStyle={{ height: 600 }}
              options={this.state.options}
              datum={chartData}
              key={(this.props?.testType || 'testType') + (this.props?.gpaType || 'CUMULATIVE_GPA')}
            />
            <Legend
              data={this.formatLegendData(chartData)}
              toggleSeries={this.toggleSeries}
              chartData={this.state.data}
            />
          </>
        ) : (
          <h4 className="scattergrams-empty-data">
            There is not enough data available to display the scattergram with the current
            settings.
          </h4>
        )}
      </div>
    );
  }
}

export default Scattergram;
